import React, { useState, useEffect, useRef } from 'react';
import { useAuthContext } from '../../context/AuthContext';
import { adminApi } from '../../services/api';
import EditableUserDetails from '../../components/admin/EditableUserDetails';
import SidebarPanel from '../../components/admin/SidebarPanel';
import UserPreferencesManager from '../../components/admin/UserPreferencesManager';

const UserProfilePage = () => {
  const { user, accessibleOrganizations, accessibleHealthSystems } = useAuthContext();
  const [loading, setLoading] = useState(true);
  const [userDetails, setUserDetails] = useState(null);
  const [error, setError] = useState(null);
  const [sidepanelOpen, setSidepanelOpen] = useState(false);
  const preferencesRef = useRef(null);

  // Scroll to preferences section
  const scrollToPreferences = () => {
    if (preferencesRef.current) {
      preferencesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    fetchUserDetails();
  }, [user]);

  const fetchUserDetails = async () => {
    if (!user) return;

    try {
      setLoading(true);
      // Fetch full user details from backend
      const response = await adminApi.get('/admin/user/profile/');

      // Handle nested user structure from API
      // API returns: { success: true, user: { id, user: { username, email, ... }, ... }, user_type: "sales" }
      if (response.data.success && response.data.user) {
        const apiUser = response.data.user;

        // Flatten the structure by merging Django user fields with SalesUser/other profile fields
        const flattenedUser = {
          ...apiUser,
          // Override with nested Django user fields if they exist
          ...(apiUser.user && {
            django_user_id: apiUser.user.id,
            username: apiUser.user.username,
            email: apiUser.user.email,
            first_name: apiUser.user.first_name,
            last_name: apiUser.user.last_name,
          }),
          user_type: response.data.user_type,
          health_system_memberships: response.data.health_system_memberships || []
        };

        setUserDetails(flattenedUser);
      } else {
        setUserDetails(response.data);
      }
      setError(null);
    } catch (err) {
      setError('Failed to load user profile');
      // Use JWT data as fallback
      setUserDetails(user);
    } finally {
      setLoading(false);
    }
  };

  // Generate gradient based on user name/email
  const getGradient = () => {
    const seed = user?.email || user?.username || 'user';
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
    }
    const hue = hash % 360;
    return `linear-gradient(135deg, hsl(${hue}, 70%, 80%), hsl(${(hue + 60) % 360}, 70%, 60%))`;
  };

  // Generate initials
  const getInitials = () => {
    if (user?.first_name && user?.last_name) {
      return `${user.first_name[0]}${user.last_name[0]}`.toUpperCase();
    } else if (user?.username) {
      return user.username.substring(0, 2).toUpperCase();
    } else if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return 'U';
  };

  // Get display name
  const getDisplayName = () => {
    if (userDetails?.first_name && userDetails?.last_name) {
      return `${userDetails.first_name} ${userDetails.last_name}`;
    } else if (user?.username) {
      return user.username;
    } else if (user?.email) {
      return user.email.split('@')[0];
    }
    return 'User';
  };

  // Get role display
  const getRoleDisplay = () => {
    if (user?.isAnkrAdmin) return 'Ankr Admin';
    if (user?.role === 'admin') return 'Admin';
    if (user?.role === 'sales') return 'Sales';
    return user?.role || 'User';
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString();
  };

  // Field configuration for editable user details
  const fieldConfig = [
    { name: 'username', label: 'Username', type: 'text', editable: true },
    { name: 'email', label: 'Email', type: 'email', editable: true },
    { name: 'first_name', label: 'First Name', type: 'text', editable: true },
    { name: 'last_name', label: 'Last Name', type: 'text', editable: true },
    { name: 'is_active', label: 'Active', type: 'checkbox', editable: true },
  ];

  const handleSaveProfile = async () => {
    await fetchUserDetails();
    setSidepanelOpen(false);
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">User Profile</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item active">Profile</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="alert alert-warning" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error} - Showing data from authentication token.
        </div>
      )}

      <div className="row">
        {/* Profile Card */}
        <div className="col-lg-4">
          <div className="card">
            <div className="card-body text-center">
              {/* Avatar */}
              <div
                className="d-inline-flex align-items-center justify-content-center text-white fw-bold rounded-circle mb-3"
                style={{
                  width: 120,
                  height: 120,
                  background: getGradient(),
                  fontSize: 48,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {getInitials()}
              </div>

              <h4 className="mb-1">{getDisplayName()}</h4>
              <p className="text-muted mb-2">{userDetails?.email || user?.email || 'No email'}</p>
              <span className={`badge bg-${user?.isAnkrAdmin ? 'danger' : 'primary'} mb-3`}>
                {getRoleDisplay()}
              </span>

              <div className="mt-4">
                <button className="btn btn-primary btn-sm w-100 mb-2" onClick={() => setSidepanelOpen(true)}>
                  <i className="fas fa-edit me-1"></i> Edit Profile
                </button>
                <button className="btn btn-outline-primary btn-sm w-100 mb-2" onClick={scrollToPreferences}>
                  <i className="fas fa-sliders-h me-1"></i> My Preferences
                </button>
                <button className="btn btn-outline-secondary btn-sm w-100">
                  <i className="fas fa-key me-1"></i> Change Password
                </button>
              </div>
            </div>
          </div>

          {/* Token Info Card */}
          {user?.isAnkrAdmin && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-shield-alt me-2"></i>
                  JWT Token Information
                </h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <small className="text-muted d-block fw-bold">Token ID</small>
                  <code className="small">{user?.id || 'N/A'}</code>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block fw-bold">Subject (sub)</small>
                  <span className="small">{user?.sub || user?.username || 'N/A'}</span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block fw-bold">Audience (aud)</small>
                  <span className="small">
                    {Array.isArray(user?.aud) ? user.aud.join(', ') : (user?.aud || 'N/A')}
                  </span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block fw-bold">Issued At (iat)</small>
                  <span className="small">{formatTimestamp(user?.iat)}</span>
                </div>
                <div className="mb-3">
                  <small className="text-muted d-block fw-bold">Expires At (exp)</small>
                  <span className="small">{formatTimestamp(user?.exp)}</span>
                </div>
                {user?.jti && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">JWT ID (jti)</small>
                    <code className="small">{user.jti}</code>
                  </div>
                )}
                {user?.scopes && user.scopes.length > 0 && (
                  <div className="mb-0">
                    <small className="text-muted d-block fw-bold mb-2">OAuth2 Scopes</small>
                    <div className="d-flex flex-wrap gap-1">
                      {user.scopes.map((scope, idx) => (
                        <span key={idx} className="badge bg-primary" style={{ fontSize: '0.75rem' }}>
                          {scope}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional User Details Card - for sales/healthcare/ankr specific fields */}
          {userDetails && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-info-circle me-2"></i>
                  Additional Details
                </h5>
              </div>
              <div className="card-body">
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Department</small>
                    <span className="small">{userDetails?.department || 'None'}</span>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Job Title</small>
                    <span className="small">{userDetails?.job_title || 'None'}</span>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Employee ID</small>
                    <code className="small">{userDetails?.employee_id || 'None'}</code>
                  </div>
                {userDetails.office_location && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Office Location</small>
                    <span className="small">{userDetails.office_location}</span>
                  </div>
                )}
                {userDetails.work_location && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Work Location</small>
                    <span className="small">{userDetails.work_location}</span>
                  </div>
                )}
                {userDetails.territory && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Territory</small>
                    <span className="small">{userDetails.territory}</span>
                  </div>
                )}
                {userDetails.region && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Region</small>
                    <span className="small">{userDetails.region}</span>
                  </div>
                )}
                {userDetails.timezone && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Timezone</small>
                    <span className="small">{userDetails.timezone}</span>
                  </div>
                )}
                {userDetails.credentials && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">Credentials</small>
                    <span className="small">{userDetails.credentials}</span>
                  </div>
                )}
                {userDetails.npi_number && (
                  <div className="mb-3">
                    <small className="text-muted d-block fw-bold">NPI Number</small>
                    <code className="small">{userDetails.npi_number}</code>
                  </div>
                )}
                {userDetails.specialties && userDetails.specialties.length > 0 && (
                  <div className="mb-0">
                    <small className="text-muted d-block fw-bold mb-1">Specialties</small>
                    <div className="d-flex flex-wrap gap-1">
                      {userDetails.specialties.map((specialty, idx) => (
                        <span key={idx} className="badge bg-info">
                          {specialty.name || specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Details Column */}
        <div className="col-lg-8">
          {/* Basic Information */}
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">
                <i className="fas fa-user me-2"></i>
                Basic Information
              </h5>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>User ID:</strong>
                </div>
                <div className="col-sm-8">
                  <code className="small">{user?.id || userDetails?.id || 'N/A'}</code>
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Username:</strong>
                </div>
                <div className="col-sm-8">
                  {userDetails?.username || user?.username || 'N/A'}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Email:</strong>
                </div>
                <div className="col-sm-8">
                  {userDetails?.email || user?.email || 'N/A'}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>First Name:</strong>
                </div>
                <div className="col-sm-8">
                  {userDetails?.first_name || user?.first_name || 'N/A'}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Last Name:</strong>
                </div>
                <div className="col-sm-8">
                  {userDetails?.last_name || user?.last_name || 'N/A'}
                </div>
              </div>
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Role:</strong>
                </div>
                <div className="col-sm-8">
                  <span className={`badge bg-${user?.isAnkrAdmin ? 'danger' : 'primary'}`}>
                    {getRoleDisplay()}
                  </span>
                </div>
              </div>
              {(userDetails?.phone || userDetails?.phone_number) && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Phone:</strong>
                  </div>
                  <div className="col-sm-8">
                    {userDetails?.phone || userDetails?.phone_number}
                  </div>
                </div>
              )}
              {userDetails?.is_active !== undefined && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Status:</strong>
                  </div>
                  <div className="col-sm-8">
                    <span className={`badge bg-${userDetails.is_active ? 'success' : 'secondary'}`}>
                      {userDetails.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              )}
              <div className="row mb-3">
                <div className="col-sm-4">
                  <strong>Token Type:</strong>
                </div>
                <div className="col-sm-8">
                  <code>{user?.tokenType || 'N/A'}</code>
                </div>
              </div>
              {(user?.organization_id || user?.organization_name) && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Primary Organization:</strong>
                  </div>
                  <div className="col-sm-8">
                    {user?.organization_name || 'N/A'}
                    {user?.organization_id && (
                      <div className="text-muted small">ID: {user.organization_id}</div>
                    )}
                  </div>
                </div>
              )}
              {user?.health_system_id && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Health System ID:</strong>
                  </div>
                  <div className="col-sm-8">
                    <code className="small">{user.health_system_id}</code>
                  </div>
                </div>
              )}
              {(user?.team_id || user?.team_name) && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Primary Team:</strong>
                  </div>
                  <div className="col-sm-8">
                    {user?.team_name || 'N/A'}
                    {user?.team_id && (
                      <div className="text-muted small">ID: {user.team_id}</div>
                    )}
                  </div>
                </div>
              )}
              {userDetails?.date_joined && (
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Date Joined:</strong>
                  </div>
                  <div className="col-sm-8">
                    {new Date(userDetails.date_joined).toLocaleDateString()}
                  </div>
                </div>
              )}
              {userDetails?.last_login && (
                <div className="row mb-0">
                  <div className="col-sm-4">
                    <strong>Last Login:</strong>
                  </div>
                  <div className="col-sm-8">
                    {new Date(userDetails.last_login).toLocaleString()}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Organizations - Show for Sales Users and Ankr Admins */}
          {(user?.isSalesUser || user?.isAnkrAdmin) && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  Sales Module
                </h5>
              </div>
              <div className="card-header border-0">
                <h5 className="card-title mb-0">
                  <i className="fas fa-building me-2"></i>
                  Organizations ({accessibleOrganizations?.length || 0})
                </h5>
              </div>
                {accessibleOrganizations && accessibleOrganizations.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {accessibleOrganizations.map((org, idx) => (
                      <div key={org.id || idx} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-1">{org.name}</h6>
                          <small className="text-muted">ID: {org.id}</small>
                        </div>
                        {org.team && (
                          <span className="badge bg-info">
                            <i className="fas fa-users me-1"></i>
                            {org.team}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-3" style={{marginLeft:"1.5rem"}}>
                    No organizations assigned
                  </p>
                )}
              <div className="card-header border-0">
                <h5 className="card-title mb-0">
                  <i className="fas fa-users me-2"></i>
                  Team Memberships ({userDetails?.team_memberships?.length || 0})
                </h5>
              </div>
                {userDetails?.team_memberships && userDetails.team_memberships.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {userDetails.team_memberships.map((membership, idx) => (
                      <div key={idx} className="list-group-item">
                        <div className="d-flex justify-content-between align-items-center">
                          <div>
                            <h6 className="mb-1">{membership.team_name}</h6>
                            <small className="text-muted">
                              Organization: {membership.organization_name}
                            </small>
                          </div>
                          {membership.role && (
                            <span className="badge bg-secondary">{membership.role}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-3" style={{marginLeft:"1.5rem"}}>
                    No team memberships
                  </p>
                )}
            </div>
          )}

          {/* Healthcare Systems - Show for Healthcare Users and Ankr Admins */}
          {(user?.isHealthcareUser || user?.isAnkrAdmin) && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">
                  <i className="fas fa-hospital-alt me-2"></i>
                  Health Systems ({accessibleHealthSystems?.length || 0})
                </h5>
              </div>
                {accessibleHealthSystems && accessibleHealthSystems.length > 0 ? (
                  <div className="list-group list-group-flush">
                    {accessibleHealthSystems.map((hs, idx) => (
                      <div key={hs.id || idx} className="list-group-item">
                        <h6 className="mb-1">{hs.name}</h6>
                        <small className="text-muted">ID: {hs.id}</small>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted m-3 pl-2">
                    No health systems assigned
                  </p>
                )}
            </div>
          )}

          {/* User Preferences */}
          <div ref={preferencesRef} className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-sliders-h me-2"></i>
                My Preferences
              </h5>
            </div>
            <div className="card-body">
              <UserPreferencesManager />
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Sidepanel */}
      <SidebarPanel
        isOpen={sidepanelOpen}
        onClose={() => setSidepanelOpen(false)}
        title={
          <>
            Edit Profile
          </>
        }
      >
        <EditableUserDetails
          userData={userDetails || user}
          userType={userDetails?.user_type || user?.tokenType}
          fieldConfig={fieldConfig}
          onSave={handleSaveProfile}
          // onError={(err) => console.error('Error saving profile:', err)}
        />
      </SidebarPanel>
    </>
  );
};

export default UserProfilePage;
